import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { FiChevronLeft } from "react-icons/fi";
import {
  MainNavList,
  MainNavButton,
  MainNavIcon,
  MainNavLabel,
  MainNavBadgeSlot,
  MainNavBackButton,
  MainNavSeparator,
  MainNavTooltip,
  MainNavTooltipTitle,
  MainNavTooltipDescription,
} from "./Styles";

export interface MainNavEntry {
  id: string;
  icon: ReactNode;
  label: string;
  description: string;
  isActive: boolean;
  onClick: () => void;
  badge?: ReactNode;
}

interface MainNavProps {
  items: MainNavEntry[];
  /** Rail shape: the destinations shrink to icons and give the column away. */
  isRail: boolean;
  /** The chevron out of the report. Only ever rendered in the rail shape. */
  back: { label: string; description: string; onClick: () => void };
}

const MORPH_DURATION_MS = 380;
const MORPH_EASING = "cubic-bezier(0.2, 0, 0, 1)";

/**
 * The app's main destinations, in one of two shapes: labelled rows everywhere
 * in the app, and a row of icons on the report page, where the sidebar belongs
 * to the report's own section nav.
 *
 * Both shapes are the same three buttons, which is what lets one turn into the
 * other: on a shape change we measure where each button was and where it has
 * landed, then play the difference back as a FLIP animation. Rendering two
 * separate lists would remount the buttons and there would be nothing to
 * animate between.
 *
 * @param items The destinations, plus the blurb each icon needs once the rail
 *   has taken its label away.
 * @param isRail Whether to draw the icon rail rather than the labelled rows.
 * @param back Where the rail's leading chevron goes.
 */
export const MainNav = ({ items, isRail, back }: MainNavProps) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const buttonsRef = useRef(new Map<string, HTMLButtonElement>());
  const rectsRef = useRef(new Map<string, DOMRect>());
  const shapeRef = useRef(isRail);

  const backEntry: MainNavEntry = {
    id: "back",
    icon: <FiChevronLeft />,
    label: back.label,
    description: back.description,
    isActive: false,
    onClick: back.onClick,
  };
  const hovered = isRail ? [backEntry, ...items].find((item) => item.id === hoveredId) : undefined;

  // Runs after every commit so the rects we keep are always the ones from the
  // shape we are about to leave.
  useLayoutEffect(() => {
    const nextRects = new Map<string, DOMRect>();
    buttonsRef.current.forEach((element, id) => {
      nextRects.set(id, element.getBoundingClientRect());
    });

    const shapeChanged = shapeRef.current !== isRail;
    shapeRef.current = isRail;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (shapeChanged && !reducedMotion) {
      nextRects.forEach((next, id) => {
        const previous = rectsRef.current.get(id);
        const element = buttonsRef.current.get(id);
        if (!previous || !element || next.width === 0 || previous.width === 0) return;

        const dx = previous.left - next.left;
        const dy = previous.top - next.top;
        if (dx === 0 && dy === 0) return;

        // Position only. Animating the button's width would reflow its
        // siblings mid-flight, which is exactly what these measurements
        // assume is not happening — so the shape snaps and only the journey
        // between the two layouts is animated.
        element.animate(
          [
            { transform: `translate(${dx}px, ${dy}px)` },
            { transform: "translate(0, 0)" },
          ],
          { duration: MORPH_DURATION_MS, easing: MORPH_EASING }
        );
      });
    }

    rectsRef.current = nextRects;
  });

  const registerButton = (id: string) => (element: HTMLButtonElement | null) => {
    if (element) {
      buttonsRef.current.set(id, element);
    } else {
      buttonsRef.current.delete(id);
    }
  };

  const hoverHandlers = (id: string) => ({
    onMouseEnter: () => setHoveredId(id),
    onMouseLeave: () => setHoveredId((current) => (current === id ? null : current)),
    onFocus: () => setHoveredId(id),
    onBlur: () => setHoveredId((current) => (current === id ? null : current)),
  });

  return (
    <MainNavList $isRail={isRail}>
      {isRail && (
        <>
          <MainNavBackButton
            type="button"
            onClick={backEntry.onClick}
            aria-label={backEntry.label}
            {...hoverHandlers(backEntry.id)}
          >
            {backEntry.icon}
          </MainNavBackButton>
          <MainNavSeparator />
        </>
      )}
      {items.map((item) => (
        <MainNavButton
          key={item.id}
          ref={registerButton(item.id)}
          data-tour={`nav-${item.id}`}
          type="button"
          $isRail={isRail}
          $isActive={item.isActive}
          onClick={item.onClick}
          aria-label={isRail ? item.label : undefined}
          {...(isRail ? hoverHandlers(item.id) : {})}
        >
          <MainNavIcon $isActive={item.isActive}>{item.icon}</MainNavIcon>
          <MainNavLabel $isActive={item.isActive} $isRail={isRail}>
            {item.label}
          </MainNavLabel>
          {item.badge && <MainNavBadgeSlot $isRail={isRail}>{item.badge}</MainNavBadgeSlot>}
        </MainNavButton>
      ))}
      {hovered && (
        <MainNavTooltip role="tooltip">
          <MainNavTooltipTitle>{hovered.label}</MainNavTooltipTitle>
          <MainNavTooltipDescription>{hovered.description}</MainNavTooltipDescription>
        </MainNavTooltip>
      )}
    </MainNavList>
  );
};
